import React, { useCallback, useRef, useState } from "react";
import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import UserAPIs from "../../apiRoutes/User";
import {
  GET_USERS_QUERY_KEY,
  GET_ROLES_QUERY_KEY,
  GET_AGENCIES_QUERY_KEY,
} from "../../utils/constants/queryKeys";
import RoleAPIs from "../../apiRoutes/Role";
import AgencyAPIs from "../../apiRoutes/Agency";
import usePermissions from "../../hooks/usePermissions";
import useUrlPaging from "../../hooks/useUrlPaging";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import ButtonLoader from "../Icons/ButtonLoader";
import { Button } from "../ui/button";
import AddUserModal from "./Modals/AddUserModal";
import UserFilterBar from "./UserFilterBar";
import useZustandStore from "../../zustandStore/useZustandStore";
import { getAgencyIdFromUser } from "../../utils/agencyScope";
import StatusChip from "../common/StatusChip";
import PaginationFooter from "../common/PaginationFooter";
import toast from "react-hot-toast";

const UserTab = () => {
  const [modal, setModal] = useState({ open: false, editData: null });
  const [deleteStep, setDeleteStep] = useState(0); // 0=none, 1=first confirm, 2=second confirm
  const [userToDelete, setUserToDelete] = useState(null);
  const [filters, setFilters] = useState(null);
  const filterKeyRef = useRef(null);
  const axiosPrivate = useAxiosPrivate();
  const { page, setPage, pageSize, setPageSize } = useUrlPaging();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const scopedAgencyId = getAgencyIdFromUser(currentUser);
  const { hasPermission } = usePermissions();
  const showAgencyFilter =
    scopedAgencyId == null && hasPermission(PERMISSION_CODES.AGENCY_MANAGE);
  const listParams = {
    ...filters?.apiParams,
    ...(scopedAgencyId != null
      ? { agency_id: scopedAgencyId, internal: undefined }
      : {}),
  };

  const handleFilters = useCallback(
    (next) => {
      if (filterKeyRef.current !== null && filterKeyRef.current !== next.key)
        setPage(0);
      filterKeyRef.current = next.key;
      setFilters(next);
    },
    [setPage]
  );

  const deleteMutation = useMutation({
    mutationFn: (id) => UserAPIs.delete({ axiosPrivate, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_USERS_QUERY_KEY });
      toast.success("User deleted.");
      setDeleteStep(0);
      setUserToDelete(null);
    },
    onError: () => {
      setDeleteStep(0);
      setUserToDelete(null);
    },
  });

  const {
    data: result,
    isLoading,
    isPlaceholderData,
  } = useQuery({
    queryKey: [...GET_USERS_QUERY_KEY, page, pageSize, listParams],
    queryFn: () =>
      UserAPIs.list({
        axiosPrivate,
        limit: pageSize,
        offset: page * pageSize,
        ...listParams,
      }),
    placeholderData: keepPreviousData,
    enabled: !!filters,
  });

  const { data: agenciesResult } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () => AgencyAPIs.list({ axiosPrivate, limit: 100, offset: 0 }),
    enabled: showAgencyFilter,
  });

  const { data: rolesResult } = useQuery({
    queryKey: GET_ROLES_QUERY_KEY,
    queryFn: () => RoleAPIs.list({ axiosPrivate, limit: 200, offset: 0 }),
  });
  const rolesList = rolesResult?.data ?? [];
  const roleNameById = Object.fromEntries(
    (rolesList || []).map((r) => [r.id, r.name])
  );

  const agencies = agenciesResult?.data ?? [];
  const hasFilters = Object.values(filters?.apiParams ?? {}).some(Boolean);
  const users = result?.data ?? [];
  const totalCount = result?.count ?? 0;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Users</h3>
        <button
          onClick={() => setModal({ open: true, editData: null })}
          className="px-4 py-2 bg-primaryPurple text-white rounded-lg text-sm font-medium hover:bg-primaryPurple/90 transition-colors"
        >
          Add User
        </button>
      </div>

      <UserFilterBar
        roles={rolesList}
        agencies={agencies}
        showAgency={showAgencyFilter}
        onChange={handleFilters}
      />

      <div className="flex flex-col min-h-[calc(100vh-200px)]">
        <div
          className={`overflow-x-auto flex-1 min-h-0 rounded-xl border border-gray-200 bg-white transition-opacity ${isPlaceholderData ? "opacity-60" : ""}`}
        >
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">
                  Name
                </th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">
                  Email
                </th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">
                  Role
                </th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">
                  Status
                </th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading || !filters ? (
                <tr>
                  <td colSpan="5" className="py-12">
                    <div className="flex items-center justify-center">
                      <ButtonLoader color="#5E30BF" />
                      <span className="ml-2 text-primaryPurple">
                        Loading Users...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="py-4 px-4 text-sm text-gray-800 font-medium">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {user.email}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600 max-w-[220px]">
                      {(user.role_ids || [])
                        .map((id) => roleNameById[id])
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </td>
                    <td className="py-4 px-4 text-sm">
                      <StatusChip subtle status={user.status} />
                    </td>
                    <td className="py-4 px-4 text-sm">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() =>
                            setModal({ open: true, editData: user })
                          }
                          className="text-primaryPurple hover:underline font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setUserToDelete(user);
                            setDeleteStep(1);
                          }}
                          className="text-red-500 hover:underline font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="py-12 text-center text-gray-400 italic"
                  >
                    {hasFilters
                      ? "No users match these filters."
                      : "No users found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationFooter
          shown={users.length}
          totalCount={totalCount}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(0);
          }}
        />
      </div>

      {modal.open && (
        <AddUserModal
          open={modal.open}
          onClose={() => setModal({ open: false, editData: null })}
          editData={modal.editData}
        />
      )}

      {/* First delete confirmation */}
      {deleteStep === 1 && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Delete user?
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                {userToDelete.first_name} {userToDelete.last_name}
              </span>{" "}
              ({userToDelete.email})?
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDeleteStep(0);
                  setUserToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setDeleteStep(2)}
              >
                Yes, delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Second (final) delete confirmation */}
      {deleteStep === 2 && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-red-600 mb-2">
              This cannot be undone
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              Permanently delete{" "}
              <span className="font-semibold">
                {userToDelete.first_name} {userToDelete.last_name}
              </span>
              ? This action cannot be reversed.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDeleteStep(0);
                  setUserToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(userToDelete.id)}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete permanently"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTab;
